import React, { useState, useEffect, useCallback } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Container, IconButton, Tabs, Tab, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    CircularProgress,
    Menu,
    MenuItem,
    Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuthContext } from '../context/AuthContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType, CaseStatus, CancerType } from '../schema';
import { getReviewerCases, ReviewerCase } from '../queries/GetReviewerCases';

const ReviewerPortal: React.FC = () => {
    const navigate = usePersistentNavigate();
    const { accessToken, logout, userType, setAccessToken } = useAuthContext();
    const [view, setView] = useState<'to-review' | 'reviewed'>('to-review');
    const [cases, setCases] = useState<ReviewerCase[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);

    // Top-right menu state & handlers
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
    const handleMenuClose = () => setMenuAnchorEl(null);
    const handleMenuHome = () => {
        handleMenuClose();
        navigate('/');
    };
    const handleMenuLogout = async () => {
        handleMenuClose();
        await logout();
    };

    const fetchCases = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            console.log(`Fetching reviewer cases with filter: ${view}, page: ${page}`);
            const response = await getReviewerCases({
                offset: page * rowsPerPage,
                pageSize: rowsPerPage,
                filter: view
            }, accessToken, setAccessToken, logout);
            setCases(response.cases);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error("Failed to fetch reviewer cases:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, view, page, rowsPerPage, setAccessToken, logout]);

    useEffect(() => {
        if (userType !== UserType.REVIEWER) {
            navigate('/');
        }
    }, [userType, navigate]);

    useEffect(() => {
        fetchCases();
    }, [fetchCases]);

    const handleViewChange = (event: React.SyntheticEvent, newValue: 'to-review' | 'reviewed') => {
        setView(newValue);
        setPage(0); // Reset page on view change
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatStatus = (status: number) => {
        switch (status) {
            case CaseStatus.PENDING_REVIEW: return 'Pending Review';
            case CaseStatus.REVIEW_FINALIZED: return 'Review Finalized';
            default: return 'Unknown';
        }
    };

    const formatCancerType = (type: number) => {
        switch (type) {
            case CancerType.BREAST: return 'Breast';
            case CancerType.THORACIC: return 'Thoracic';
            case CancerType.GI: return 'GI';
            case CancerType.GU: return 'GU';
            case CancerType.GYNO: return 'Gyno';
            case CancerType.HEAD_NECK: return 'Head/Neck';
            case CancerType.NEURO: return 'Neuro';
            case CancerType.SKIN: return 'Skin';
            case CancerType.HEME: return 'Heme';
            case CancerType.OTHER: return 'Other';
            default: return 'Not Provided';
        }
    };

    const formatSubmitTime = (date?: Date) => {
        if (!date) return '-';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (cases.length === 0) {
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Case ID</TableCell>
                                <TableCell>Cancer Type</TableCell>
                                <TableCell>Submit Time</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No cases available
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Case ID</TableCell>
                            <TableCell>Cancer Type</TableCell>
                            <TableCell>Submit Time</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cases.map((c) => (
                            <TableRow key={c.id}>
                                <TableCell>{c.id.substring(0, 8)}...</TableCell>
                                <TableCell>{formatCancerType(c.cancerType)}</TableCell>
                                <TableCell>{formatSubmitTime(c.submitTime)}</TableCell>
                                <TableCell>{formatStatus(c.status)}</TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<DescriptionIcon />}
                                        onClick={() => navigate(view === 'to-review' ? `/review-case?case_id=${c.id}` : `/case-report?case_id=${c.id}&from=reviewer`)}
                                    >
                                        {view === 'to-review' ? 'Review' : 'View Report'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        );
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box
                        component="a"
                        href="/"
                        sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                        <Box
                            component="img"
                            src="/logo.jpg"
                            alt="Precisca"
                            sx={{
                                height: { xs: 28, sm: 36 },
                                width: 'auto',
                                display: 'block',
                                mr: 1
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            sx={{ color: '#2c5282' }}
                            aria-controls={menuOpen ? 'top-right-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen ? 'true' : undefined}
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="top-right-menu"
                            anchorEl={menuAnchorEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={handleMenuHome}>Home</MenuItem>
                            <MenuItem onClick={handleMenuLogout}>Log Out</MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ mx: 2 }}>
                    <Tabs value={view} onChange={handleViewChange} indicatorColor="primary" textColor="primary">
                        <Tab label="Cases to Review" value="to-review" />
                        <Tab label="Previously Reviewed Cases" value="reviewed" />
                    </Tabs>
                    {renderTable()}
                </Paper>
            </Container>
        </Box>
    );
};

export default ReviewerPortal;
